import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Collection } from "../../types";
import { fetchCollections, updateCollection } from "./actions";
import { RootState } from "../store";
import { CollectionsFormData } from "../../types";
import _ from "lodash";

interface PaginationModel {
  page: number;
  pageSize: number;
}
interface SortModel {
  order: "asc" | "desc";
  orderBy: keyof Collection;
}

interface CollectionsState {
  collections: Collection[];
  count: number;
  selectedCollection: Collection | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  paginationModel: PaginationModel;
  formData: CollectionsFormData;
  sortModel: SortModel;
}

const initialState: CollectionsState = {
  collections: [],
  count: 0,
  selectedCollection: null,
  status: "idle",
  error: null,
  paginationModel: {
    page: 0,
    pageSize: -1,
  },
  formData: {
    selectedDevice: [],
    startingDate: null,
    endingDate: null,
    trapTypes: [],
    collectionSites: [],
  },
  sortModel: {
    order: "desc",
    orderBy: "createdAt",
  },
};

const CollectionsSlice = createSlice({
  name: "collections",
  initialState,
  reducers: {
    setSortModel: (state, action: PayloadAction<SortModel>) => {
      state.sortModel = action.payload;
      state.collections.sort((a, b) => {
        const valueA = a.createdAt;
        const valueB = b.createdAt;

        let comparison = 0;
        if (valueA < valueB) {
          comparison = -1;
        } else if (valueA > valueB) {
          comparison = 1;
        }

        if (comparison !== 0) {
          return state.sortModel.order === "asc" ? comparison : -comparison;
        }
        return 0;
      });
    },
    clearCollections: (state) => {
      state.collections = [];
    },
    setPaginationModel: (state, action: PayloadAction<PaginationModel>) => {
      state.paginationModel = action.payload;
    },
    setFormData: (state, action: PayloadAction<CollectionsFormData>) => {
      state.formData = action.payload;
    },
    selectCollection: (state, action: PayloadAction<string>) => {
      state.selectedCollection =
        state.collections.find((c) => c.id === action.payload) || null;
    },
    goToPreviousCollection: (state) => {
      if (state.selectedCollection && state.collections.length > 1) {
        const currentIndex = state.collections.findIndex(
          (c) => c.id === state.selectedCollection?.id
        );
        const previousIndex =
          currentIndex === 0 ? state.collections.length - 1 : currentIndex - 1;
        state.selectedCollection = state.collections[previousIndex];
      }
    },
    goToNextCollection: (state) => {
      if (state.selectedCollection && state.collections.length > 1) {
        const currentIndex = state.collections.findIndex(
          (c) => c.id === state.selectedCollection?.id
        );
        const nextIndex =
          currentIndex === state.collections.length - 1 ? 0 : currentIndex + 1;
        state.selectedCollection = state.collections[nextIndex];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCollections.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCollections.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload) {
          state.collections = _.uniqBy(
            [...state.collections, ...action.payload?.data],
            "id"
          );

          state.collections.sort((a, b) => {
            const valueA = a.createdAt;
            const valueB = b.createdAt;

            let comparison = 0;
            if (valueA < valueB) {
              comparison = -1;
            } else if (valueA > valueB) {
              comparison = 1;
            }

            if (comparison !== 0) {
              return state.sortModel.order === "asc" ? comparison : -comparison;
            }
            return 0;
          });

          state.count = action.payload.count || 0;
        }
      })
      .addCase(fetchCollections.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(
        updateCollection.fulfilled,
        (state, action: PayloadAction<Collection>) => {
          let collection: Collection = action.payload;
          state.collections = state.collections.map((cur) => {
            if (cur.id === collection.id) {
              return { ...cur, ...collection };
            }
            return cur;
          });
        }
      );
  },
});

export const {
  selectCollection,
  goToPreviousCollection,
  goToNextCollection,
  setPaginationModel,
  setFormData,
  clearCollections,
  setSortModel,
} = CollectionsSlice.actions;

// Selectors
export const selectAllCollections = (state: RootState) =>
  state.collections.collections;
export const selectSelectedCollection = (state: RootState) => {
  if (state.collections.selectedCollection) {
    return state.collections.selectedCollection;
  }
  return {
    displayName: "",
    deviceUuid: "",
    createdAt: "",
    description: "",
    trapId: "",
    collectionSiteId: "",
    trapTypeId: "",
    collectionDate: "",
  } as Collection;
};
export const selectCollectionStatus = (state: RootState) =>
  state.collections.status;
export const selectCollectionError = (state: RootState) =>
  state.collections.error;

export default CollectionsSlice.reducer;
